import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The one ✕ that closes a surface — every dialog, sheet, drawer, panel and
 * card that can be dismissed (Andrew, 2026-11-03: "it looks to be a
 * different size and position on all dialogs. Please consolidate them").
 * They had drifted to five sizes: 16px and 18px glyphs in 28px and 40px
 * circles, a ghost Button, and the dialog's own.
 *
 * The dialog's version is the one kept: a 20px ✕ in a 44px round target,
 * muted until hovered. Two placements:
 *
 *   corner  absolute, 12px from the surface's top-right — what Dialog and
 *           Sheet draw, for a surface whose header leaves the corner free
 *   inline  in a header row, its right edge pulled 8px into the padding so
 *           the glyph sits where a corner ✕ would
 *
 * Use it inside a Radix `Close` with `asChild` where the surface is a Radix
 * dialog, or with `onClick` anywhere else.
 */
export const CLOSE_BUTTON_CLASS = cn(
  "flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-full text-muted-foreground",
  "transition-colors duration-instant ease-standard hover:bg-foreground/[.06] hover:text-foreground",
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
  "disabled:pointer-events-none disabled:opacity-40",
);

/** Where the corner ✕ sits: the dialog's and the sheet's spot. */
export const CLOSE_CORNER_CLASS = "absolute right-3 top-3 z-raised";

/** On the sidebar's dark ground (the phone's More sheet), the same ✕ in the sidebar's ink. */
const SIDEBAR_TONE = "text-sidebar-foreground/80 hover:bg-white/[.06] hover:text-sidebar-bright focus-visible:ring-sidebar-on-active";

export interface CloseButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** What closes, for a screen reader: "Close", "Close Notifications". */
  label: string;
  placement?: "corner" | "inline";
  tone?: "default" | "sidebar";
}

export const CloseButton = React.forwardRef<HTMLButtonElement, CloseButtonProps>(
  ({ label, placement = "inline", tone = "default", className, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={cn(
        CLOSE_BUTTON_CLASS,
        placement === "corner" ? CLOSE_CORNER_CLASS : "-mr-2",
        tone === "sidebar" && SIDEBAR_TONE,
        className,
      )}
      {...props}
    >
      <X className="h-5 w-5" aria-hidden="true" />
    </button>
  ),
);
CloseButton.displayName = "CloseButton";
